import { z } from "zod";

// Permission string literals — exhaustive set for Sprint 1-2
export const permissionSchema = z.enum([
  "apartment.read_address",
  "apartment.read_access_info",
  "apartment.read_details",
  "cleaning_job.read",
  "cleaning_job.complete",
  "cleaning_job.upload_photos",
  "cleaning_job.assign",
  "maintenance_job.read",
  "maintenance_job.complete",
  "maintenance_job.upload_photos",
  "maintenance_job.create",
  "reservation.read_schedule",
  "reservation.read_contact",
  "reservation.read_pii",
]);

export type Permission = z.infer<typeof permissionSchema>;
export const permissionsArraySchema = z.array(permissionSchema);

// Revocation policy (inside terms)
const revocationPolicySchema = z.object({
  in_progress_jobs: z.enum(["complete_then_revoke", "immediate_revoke"]).default("complete_then_revoke"),
  reassign_on_terminate: z.boolean().default(false),
});

// Fee structure (inside terms)
const feeStructureSchema = z.object({
  platform_fee_pct: z.number().min(0).max(1).default(0),
});

// Rate card: per-bedroom pricing set by the service org, customer-specific
// Keys are bedroom counts as strings: "0" (studio), "1", "2", "3", "4+"
const rateCardSchema = z.record(z.string(), z.number().nonnegative());
export type RateCard = z.infer<typeof rateCardSchema>;

// Terms schemas per contract_type
export const serviceEngagementTermsSchema = z.object({
  // Defined by the PM org (whoever has the apartments)
  scope: z.object({
    apartment_ids: z.array(z.number().int().positive()),
  }).optional(),
  permissions: permissionsArraySchema.optional(),
  // Defined by the service org — rates per bedroom count, customer-specific
  rate_card: rateCardSchema.optional(),
  fee_structure: feeStructureSchema,
  invoicing_cadence: z.enum(["weekly", "biweekly", "monthly"]).default("monthly"),
  revocation_policy: revocationPolicySchema.optional(),
  amends_contract_id: z.number().int().optional(),
});

export const internalManagementTermsSchema = z.object({
  fee_structure: feeStructureSchema,
  invoicing_cadence: z.enum(["weekly", "biweekly", "monthly"]).default("monthly"),
});

export const termsSchemaByContractType = {
  service_engagement: serviceEngagementTermsSchema,
  internal_management: internalManagementTermsSchema,
} as const;

export type ContractType = keyof typeof termsSchemaByContractType;

export function validateTerms(contractType: ContractType, terms: unknown) {
  const schema = termsSchemaByContractType[contractType];
  return schema.safeParse(terms);
}
